/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { addDays, isValid, parseISO, startOfDay } from 'date-fns';
import {
  DEFAULT_DEPARTURE_OFFSET_DAYS,
  DEFAULT_RETURN_OFFSET_DAYS,
} from '../configs/constants';
import type { SearchFormState, SnapRecentSearchEntry, Station } from '../types';
import { TripType } from '../types';
import { getNormalizedSnapListingDates } from './snapListingContext';
import { mapPurchasesStationToSnap } from './stationUtils';
import { resolveRecentSearch } from './utils';

export type SnapDeepLinkParams = {
  orig?: string;
  dest?: string;
  fromDateFlight?: string;
  toDateFlight?: string;
  fromDateHotel?: string;
  toDateHotel?: string;
  rooms?: string;
  adults?: string;
  children?: string;
  infants?: string;
  tripType?: string;
};

export type SnapDeepLinkRouteParams = Record<
  string,
  string | string[] | boolean | undefined
>;

type BannerRedirectionResult =
  | { type: 'hotel-search'; queryString: string }
  | { type: 'external' };

export const getDefaultFormState = (): SearchFormState => {
  const today = startOfDay(new Date());
  const defaultDepart = addDays(today, DEFAULT_DEPARTURE_OFFSET_DAYS);
  const defaultReturn = addDays(today, DEFAULT_RETURN_OFFSET_DAYS);

  return {
    tripType: TripType.ROUND_TRIP,
    selectedDates: {
      depart: defaultDepart,
      return: defaultReturn,
    },
    hotelDates: {
      checkIn: defaultDepart,
      checkOut: defaultReturn,
    },
    selectDifferentDates: false,
    rooms: [{ id: '1', adults: 2, children: [], infants: 0 }],
    originStation: null,
    destinationStation: null,
  };
};

const getStringParam = (
  value: string | string[] | boolean | undefined,
): string | undefined => {
  if (typeof value === 'boolean') {
    return undefined;
  }

  if (Array.isArray(value)) {
    return value[0];
  }

  return value;
};

const getBooleanParam = (
  value: string | string[] | boolean | undefined,
): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }

  const stringValue = getStringParam(value);
  return stringValue === 'true';
};

export const parseSnapDeepLinkParams = (
  routeParams: SnapDeepLinkRouteParams,
): SnapDeepLinkParams => {
  const routeOrig = getStringParam(routeParams.orig);
  const routeDest = getStringParam(routeParams.dest);
  const fromDateFlight = getStringParam(routeParams.fromDateFlight);
  const toDateFlight = getStringParam(routeParams.toDateFlight);
  const fromDateHotel = getStringParam(routeParams.fromDateHotel);
  const toDateHotel = getStringParam(routeParams.toDateHotel);
  const normalizedDates = getNormalizedSnapListingDates({
    fromDateFlight: fromDateFlight || '',
    fromDateHotel: fromDateHotel || fromDateFlight || '',
    toDateFlight: toDateFlight || '',
    toDateHotel: toDateHotel || toDateFlight || '',
  });
  const hasListingParams = Boolean(
    fromDateFlight ||
      toDateFlight ||
      fromDateHotel ||
      toDateHotel ||
      getStringParam(routeParams.rooms) ||
      getStringParam(routeParams.adults),
  );

  if (!getBooleanParam(routeParams.isDeepLink) && !hasListingParams) {
    return {};
  }

  return Object.fromEntries(
    Object.entries({
      orig: routeOrig,
      dest: routeDest,
      fromDateFlight: normalizedDates.fromDateFlight || undefined,
      toDateFlight: normalizedDates.toDateFlight || undefined,
      fromDateHotel: normalizedDates.fromDateHotel || undefined,
      toDateHotel: normalizedDates.toDateHotel || undefined,
      rooms: getStringParam(routeParams.rooms),
      adults: getStringParam(routeParams.adults),
      children: getStringParam(routeParams.children),
      infants: getStringParam(routeParams.infants),
      tripType: getStringParam(routeParams.tripType),
    }).filter(([, value]) => value !== undefined),
  );
};

export const hasSnapDeepLinkParams = ({
  orig,
  dest,
}: SnapDeepLinkParams): boolean => Boolean(orig || dest);

const getStringQueryParam = (
  queryParams: Record<string, string>,
  ...keys: string[]
): string | undefined => keys.map((key) => queryParams[key]).find(Boolean);

const hasHotelSearchCriteria = (queryParams: Record<string, string>): boolean =>
  Boolean(
    getStringQueryParam(
      queryParams,
      'locationId',
      'destination',
      'checkin',
      'fromDate',
      'checkout',
      'toDate',
      'adults',
      'rooms',
      'children',
    ),
  );

export const resolveBannerRedirection = (
  redirectionUrl: string,
): BannerRedirectionResult => {
  try {
    const parsed = new URL(redirectionUrl);
    const path = parsed.pathname;

    const isHotelSearchPath =
      path.includes('/hotel/search') ||
      /\/hotels\/[^/]+\/[^/]+\/search/.test(path);

    if (!isHotelSearchPath) {
      return { type: 'external' };
    }

    const queryParams = Object.fromEntries(parsed.searchParams.entries());
    if (!hasHotelSearchCriteria(queryParams)) {
      return { type: 'external' };
    }

    return { type: 'hotel-search', queryString: parsed.search };
  } catch {
    return { type: 'external' };
  }
};

const parseRecentSearchDate = (value?: string): Date | undefined => {
  if (!value) {
    return undefined;
  }

  const parsedDate = parseISO(value);
  return isValid(parsedDate) ? parsedDate : undefined;
};

const parseListingRoomCounts = ({
  adults,
  children,
  infants,
  rooms,
}: {
  adults?: string;
  children?: string;
  infants?: string;
  rooms?: string;
}) => {
  const roomCount = Math.max(Number(rooms) || 1, 1);
  const adultCounts = adults
    ?.split(',')
    .map((adultCount) => Number(adultCount))
    .filter((adultCount) => Number.isFinite(adultCount) && adultCount > 0);
  const childAgeEntries = new Map<number, { id: string; age: number }[]>();
  const infantCounts = new Map<number, number>();

  children
    ?.split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .forEach((entry, index) => {
      const [roomIndexValue, ageValue] = entry.split('_');
      const roomIndex = Math.max(Number(roomIndexValue) - 1, 0);
      const age = Number(ageValue);

      if (!Number.isFinite(age)) {
        return;
      }

      childAgeEntries.set(roomIndex, [
        ...(childAgeEntries.get(roomIndex) || []),
        { id: `${roomIndex + 1}_${index}`, age },
      ]);
    });

  infants
    ?.split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .forEach((entry) => {
      const [roomIndexValue] = entry.split('_');
      const roomIndex = Math.max(Number(roomIndexValue) - 1, 0);
      infantCounts.set(roomIndex, (infantCounts.get(roomIndex) || 0) + 1);
    });

  return Array.from({ length: roomCount }, (_, index) => ({
    id: `${index + 1}`,
    adults: adultCounts?.[index] || adultCounts?.[0] || 1,
    children: childAgeEntries.get(index) || [],
    infants: infantCounts.get(index) || 0,
  }));
};

const resolveStationFromMap = (
  stationsMap: Record<string, unknown> | undefined,
  stationCode?: string,
): Station | null => {
  if (!stationsMap || !stationCode) {
    return null;
  }

  const matchedStation = stationsMap[stationCode.toUpperCase()];
  if (!matchedStation) {
    return null;
  }

  return mapPurchasesStationToSnap(
    matchedStation as Parameters<typeof mapPurchasesStationToSnap>[0],
  );
};

export const buildSnapDeepLinkInitialState = ({
  deeplinkParams,
  latestRecentSearch,
  stationsMap,
}: {
  deeplinkParams: SnapDeepLinkParams;
  latestRecentSearch?: SnapRecentSearchEntry;
  stationsMap?: Record<string, unknown>;
}): SearchFormState => {
  const defaultState = getDefaultFormState();

  if (!hasSnapDeepLinkParams(deeplinkParams) && !latestRecentSearch) {
    return defaultState;
  }

  const resolvedRecentSearch = latestRecentSearch
    ? resolveRecentSearch(latestRecentSearch)
    : null;

  const recentDepartDate = parseRecentSearchDate(
    resolvedRecentSearch?.departDate,
  );
  const recentReturnDate = parseRecentSearchDate(
    resolvedRecentSearch?.returnDate,
  );
  const hasListingDateParams = Boolean(
    deeplinkParams.fromDateFlight || deeplinkParams.toDateFlight,
  );
  const isListingOneWay = hasListingDateParams && !deeplinkParams.toDateFlight;
  const isOneWay =
    isListingOneWay || resolvedRecentSearch?.tripType === TripType.ONE_WAY;
  const selectedDates = {
    depart:
      parseRecentSearchDate(deeplinkParams.fromDateFlight) ??
      recentDepartDate ??
      defaultState.selectedDates.depart,
    return: isOneWay
      ? undefined
      : (parseRecentSearchDate(deeplinkParams.toDateFlight) ??
        recentReturnDate ??
        defaultState.selectedDates.return),
  };
  const hasDifferentHotelDates =
    Boolean(deeplinkParams.fromDateHotel) &&
    deeplinkParams.fromDateHotel !== deeplinkParams.fromDateFlight;
  const selectDifferentDates =
    hasDifferentHotelDates ||
    (resolvedRecentSearch?.selectDifferentDates ?? false);
  const recentHotelCheckIn = parseRecentSearchDate(
    resolvedRecentSearch?.hotelCheckIn,
  );
  const recentHotelCheckOut = parseRecentSearchDate(
    resolvedRecentSearch?.hotelCheckOut,
  );
  const recentRooms = resolvedRecentSearch?.rooms ?? [];
  const listingRooms = parseListingRoomCounts({
    adults: deeplinkParams.adults,
    children: deeplinkParams.children,
    infants: deeplinkParams.infants,
    rooms: deeplinkParams.rooms,
  });

  const originStation = resolveStationFromMap(stationsMap, deeplinkParams.orig);
  const destinationStation = resolveStationFromMap(
    stationsMap,
    deeplinkParams.dest,
  );

  return {
    ...defaultState,
    tripType: isOneWay
      ? TripType.ONE_WAY
      : (resolvedRecentSearch?.tripType ?? defaultState.tripType),
    selectedDates,
    hotelDates: selectDifferentDates
      ? {
          checkIn:
            parseRecentSearchDate(deeplinkParams.fromDateHotel) ??
            recentHotelCheckIn ??
            selectedDates.depart,
          checkOut:
            parseRecentSearchDate(deeplinkParams.toDateHotel) ??
            recentHotelCheckOut ??
            selectedDates.return,
        }
      : {
          checkIn:
            parseRecentSearchDate(deeplinkParams.fromDateHotel) ??
            selectedDates.depart,
          checkOut:
            parseRecentSearchDate(deeplinkParams.toDateHotel) ??
            selectedDates.return,
        },
    selectDifferentDates,
    rooms: deeplinkParams.rooms
      ? listingRooms
      : recentRooms.length > 0
        ? recentRooms
        : defaultState.rooms,
    originStation,
    destinationStation,
  };
};

export type SnapDeepLinkRoute = {
  params: SnapDeepLinkRouteParams | null;
  /** Flights add-ons return link (`/snap/trip-summary?snapId=&contextId=`). */
  isTripSummaryScreen: boolean;
  /** Expo Router stack screen name used as `initialRouteName`. */
  initialRouteName: 'index';
  /** Expo location path for `ExpoRoot` / `router.replace` (includes query). */
  expoLocation: string | undefined;
};

const TRIP_SUMMARY_PATH_RE = /(?:^|\/)snap\/trip-summary(?:\/|$)/i;

/**
 * Normalizes app-scheme and https SNAP URLs to a comparable path:
 * - `airasia://snap/trip-summary` → `snap/trip-summary`
 * - `https://www.airasia.com/snap/trip-summary` → `snap/trip-summary`
 * - `/snap/trip-summary` → `snap/trip-summary`
 */
export const normalizeSnapDeeplinkPath = (url: string): string => {
  const trimmed = url.trim();
  try {
    if (trimmed.includes('://')) {
      const parsed = new URL(trimmed);
      if (/^https?:$/i.test(parsed.protocol)) {
        return parsed.pathname.replace(/^\/+|\/+$/g, '').toLowerCase();
      }
      // App scheme: host is the LOB (`snap`), pathname is the screen.
      return `${parsed.host}${parsed.pathname}`
        .replace(/^\/+|\/+$/g, '')
        .toLowerCase();
    }
  } catch {
    // Fall through to slash-based strip.
  }

  return trimmed
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
    .split(/[?#]/)[0]
    .replace(/^\/+|\/+$/g, '')
    .toLowerCase();
};

const queryParamsFromUrl = (url: string): SnapDeepLinkRouteParams => {
  const queryIndex = url.indexOf('?');
  if (queryIndex < 0) {
    return {};
  }

  const query = url.slice(queryIndex + 1).split('#')[0] ?? '';
  if (!query) {
    return {};
  }

  const params: SnapDeepLinkRouteParams = {};
  new URLSearchParams(query).forEach((value, key) => {
    params[key] = value;
  });
  return params;
};

const buildExpoQuery = (params: SnapDeepLinkRouteParams): string => {
  const entries = Object.entries(params).reduce<Record<string, string>>(
    (acc, [key, value]) => {
      if (typeof value === 'string') {
        acc[key] = value;
      }
      return acc;
    },
    {},
  );
  const query = new URLSearchParams(entries).toString();
  return query ? `?${query}` : '';
};

/**
 * Resolves a Super App / move-app SNAP deeplink into the Expo stack route.
 * Maps Trip Summary (`/snap/trip-summary`); extend as more paths land.
 */
export const parseSnapDeepLinkRoute = (
  url: string | null | undefined,
): SnapDeepLinkRoute => {
  if (!url?.trim()) {
    return {
      params: null,
      isTripSummaryScreen: false,
      initialRouteName: 'index',
      expoLocation: undefined,
    };
  }

  const normalizedPath = normalizeSnapDeeplinkPath(url);
  const isTripSummaryScreen = TRIP_SUMMARY_PATH_RE.test(normalizedPath);
  const queryParams = queryParamsFromUrl(url);

  if (isTripSummaryScreen) {
    // Flights add-ons returns here with only snapId and contextId.
    return {
      params: { ...queryParams, isDeepLink: true },
      isTripSummaryScreen: true,
      initialRouteName: 'index',
      expoLocation: `/trip-summary${buildExpoQuery(queryParams)}`,
    };
  }

  return {
    params: Object.keys(queryParams).length
      ? { ...queryParams, isDeepLink: true }
      : null,
    isTripSummaryScreen: false,
    initialRouteName: 'index',
    expoLocation: undefined,
  };
};

/** Maps a SNAP deeplink URL to an Expo Router location inside `Snap.tsx`. */
export const mapSnapDeeplinkToExpoLocation = (
  url: string | null | undefined,
): string | undefined => parseSnapDeepLinkRoute(url).expoLocation;
